import { useCallback, useEffect, useState } from "react";
import { apiPost } from "../lib/api";
import type { CostEstimate } from "../lib/types";
import { useDeskSpend } from "../context/DeskSpendContext";

type Phase = "phase1_execute" | "phase2_extract" | "phase3_synthesize";

type Props = {
  phase: Phase;
  jobId?: string;
  model?: string;
  force?: boolean;
  bucketCount?: number;
  /** Re-run estimate when this changes (e.g. approved query count). */
  estimateKey?: string | number;
  approvedQueueItems?: unknown[];
  searchEngines?: string[];
  cheapModel?: string;
  proModel?: string;
  onReadyChange?: (ready: boolean) => void;
  /** Inline strip for toolbars — no bordered panel. Default on. */
  compact?: boolean;
  /** Full bordered panel with breakdown (overrides compact). */
  detailed?: boolean;
};

function formatUsd(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export function CostAcknowledgementGate({
  phase,
  jobId,
  model,
  force = false,
  bucketCount = 1,
  estimateKey = "",
  approvedQueueItems,
  searchEngines,
  cheapModel,
  proModel,
  onReadyChange,
  compact = true,
  detailed = false,
}: Props) {
  const { spend } = useDeskSpend();
  const [estimate, setEstimate] = useState<CostEstimate | null>(null);
  const [ack, setAck] = useState(false);
  const [loading, setLoading] = useState(false);

  const phaseBlocked =
    spend.desk_locked ||
    (phase === "phase2_extract" && spend.evidence_cap_reached && !force);

  const needsModel = phase === "phase2_extract" || phase === "phase3_synthesize";
  const modelReady = !needsModel || Boolean(model?.trim());

  const fetchEstimate = useCallback(async () => {
    if (phaseBlocked || !modelReady) {
      setEstimate(null);
      setAck(false);
      return;
    }
    setLoading(true);
    try {
      const body: Record<string, unknown> = {
        phase,
        force,
        bucket_count: bucketCount,
      };
      if (jobId) body.job_id = jobId;
      if (model) body.model = model;
      if (phase === "phase1_execute") {
        body.approved_queue_items = approvedQueueItems || [];
        body.search_engines = searchEngines || [];
        body.openrouter_model_cheap = cheapModel || "";
        body.openrouter_model_pro = proModel || "";
      }
      const data = await apiPost<CostEstimate & { ok?: boolean }>("/api/jobs/estimate", body);
      setEstimate(data);
      setAck(false);
    } catch {
      setEstimate(null);
      setAck(false);
    } finally {
      setLoading(false);
    }
  }, [
    phase,
    jobId,
    model,
    force,
    bucketCount,
    phaseBlocked,
    approvedQueueItems,
    searchEngines,
    cheapModel,
    proModel,
    modelReady,
  ]);

  useEffect(() => {
    const t = window.setTimeout(() => void fetchEstimate(), 400);
    return () => window.clearTimeout(t);
  }, [fetchEstimate, estimateKey]);

  const nothingToProcess =
    phase === "phase2_extract" &&
    !force &&
    estimate != null &&
    (estimate.pages_to_process ?? estimate.breakdown?.[0]?.units ?? 0) === 0;

  const ready =
    Boolean(ack) &&
    Boolean(estimate) &&
    !estimate?.would_exceed_budget &&
    !phaseBlocked &&
    !nothingToProcess;

  useEffect(() => {
    onReadyChange?.(ready);
  }, [ready, onReadyChange]);

  const ackDisabled =
    Boolean(estimate?.would_exceed_budget) || phaseBlocked || nothingToProcess;

  const unitsLabel =
    phase === "phase3_synthesize" && bucketCount > 1
      ? `${bucketCount} buckets`
      : phase === "phase3_synthesize"
        ? "1 bucket"
        : phase === "phase2_extract" && estimate
          ? `${estimate.pages_to_process ?? estimate.breakdown?.[0]?.units ?? 0} pages`
          : null;

  const useCompact = compact && !detailed;

  if (useCompact) {
    return (
      <div
        className="flex flex-wrap items-center gap-x-3 gap-y-1 text-body-sm text-on-surface-variant shrink-0"
        title="Pre-flight worst-case API cost for this action"
      >
        {loading ? (
          <span>Calculating…</span>
        ) : !modelReady ? (
          <span>Select model</span>
        ) : estimate ? (
          <>
            <span className="tabular-nums">
              Est.{" "}
              <strong className="text-on-surface font-semibold">
                {formatUsd(estimate.estimate_usd)}
              </strong>
              {unitsLabel ? ` · ${unitsLabel}` : ""}
            </span>
            {estimate.would_exceed_budget ? (
              <span className="text-error font-medium">Would exceed budget</span>
            ) : nothingToProcess ? (
              <span>Nothing to process</span>
            ) : (
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded-none border-outline-variant text-primary focus:ring-0"
                  checked={ack}
                  disabled={ackDisabled}
                  onChange={(e) => setAck(e.target.checked)}
                />
                <span>Acknowledge cost</span>
              </label>
            )}
          </>
        ) : (
          <span>Estimate unavailable</span>
        )}
      </div>
    );
  }

  return (
    <div className="border border-outline-variant/60 bg-surface-container/40 p-4 space-y-3">
      <p className="font-data-label text-data-label uppercase tracking-widest text-on-surface-variant">
        Pre-flight cost estimate
      </p>
      {loading ? (
        <p className="text-body-sm text-outline-variant">Calculating worst-case cost…</p>
      ) : estimate ? (
        <>
          <p className="text-body-base">
            Worst-case estimate:{" "}
            <strong className="text-primary tabular-nums">
              {formatUsd(estimate.estimate_usd)}
            </strong>
            {nothingToProcess ? (
              <span className="text-on-surface-variant block mt-1 text-body-sm">
                No eligible pages pending extraction for this job.
              </span>
            ) : null}
            {estimate.would_exceed_budget ? (
              <span className="text-error block mt-1 text-body-sm">
                Would exceed job budget (${estimate.budget_usd.toFixed(2)} cap, $
                {estimate.spend_usd.toFixed(2)} already spent).
              </span>
            ) : null}
          </p>
          {estimate.breakdown?.length ? (
            <ul className="text-[11px] text-on-surface-variant space-y-0.5 max-h-24 overflow-y-auto">
              {estimate.breakdown.map((line, i) => (
                <li key={`${line.operation}-${i}`}>
                  {line.provider} · {line.operation}: {line.units} ×{" "}
                  {formatUsd(line.unit_cost_usd)} = {formatUsd(line.subtotal_usd)}
                </li>
              ))}
            </ul>
          ) : null}
          <label className="flex items-start gap-2 cursor-pointer text-body-sm">
            <input
              type="checkbox"
              className="mt-0.5 rounded-none border-outline-variant text-primary focus:ring-0"
              checked={ack}
              disabled={ackDisabled}
              onChange={(e) => setAck(e.target.checked)}
            />
            <span>
              I understand this action may incur up to{" "}
              <strong>{formatUsd(estimate.estimate_usd)}</strong> in API charges for this
              job.
            </span>
          </label>
        </>
      ) : (
        <p className="text-body-sm text-outline-variant">Estimate unavailable.</p>
      )}
    </div>
  );
}
